import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Car, MapPin, Plus, Loader2, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TRIP_TYPES = [
  { v: "airport_pickup", l: "t_airport_pickup" },
  { v: "airport_dropoff", l: "t_airport_dropoff" },
  { v: "hotel_to_clinic", l: "t_hotel_to_clinic" },
  { v: "clinic_to_hotel", l: "t_clinic_to_hotel" },
  { v: "other", l: "t_other" },
];
const TRIP_STATUS_STYLE = {
  scheduled: "bg-blue-100 text-blue-700",
  en_route_pickup: "bg-amber-100 text-amber-700",
  arrived_pickup: "bg-amber-100 text-amber-700",
  passenger_onboard: "bg-indigo-100 text-indigo-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-200 text-slate-500",
};
const label = (s) => (s || "").replace(/_/g, " ");
function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function Transportation() {
  const { can } = useAuth();
  const t = useT();
  const [tab, setTab] = useState("trips");
  const canCreate = can("transportation", "create");
  const canEdit = can("transportation", "edit");

  return (
    <div className="p-6 sm:p-8 max-w-[1300px]">
      <PageHeader title={t("transport.title")} subtitle={t("transport.subtitle")}>
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="transport-tabs">
          {[["trips", t("transport.trips")], ["vehicles", t("transport.vehicles")]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${tab === v ? "bg-navy text-white" : "text-slate-500 hover:text-navy"}`}>{l}</button>
          ))}
        </div>
      </PageHeader>

      {tab === "trips" && <Trips canCreate={canCreate} canEdit={canEdit} tr={t} />}
      {tab === "vehicles" && <Vehicles canCreate={canCreate} canEdit={canEdit} tr={t} />}
    </div>
  );
}

function Trips({ canCreate, canEdit, tr }) {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refs, setRefs] = useState({ patients: [], drivers: [], vehicles: [] });
  const [form, setForm] = useState({ patient_id: "", driver_id: "", vehicle_id: "", type: "airport_pickup", date: "", time: "10:00", pickup_location: "", dropoff_location: "", notes: "" });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/transport/trips").then((r) => setTrips(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([api.get("/patients"), api.get("/meta/drivers"), api.get("/meta/vehicles")])
      .then(([p, d, v]) => setRefs({ patients: p.data, drivers: d.data, vehicles: v.data })).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id || !form.driver_id || !form.date) return toast.error(tr("transport.requiredFields"));
    setSaving(true);
    try {
      await api.post("/transport/trips", {
        patient_id: form.patient_id, driver_id: form.driver_id, vehicle_id: form.vehicle_id || null,
        type: form.type, scheduled_time: `${form.date}T${form.time}:00`,
        pickup_location: form.pickup_location, dropoff_location: form.dropoff_location, notes: form.notes,
      });
      toast.success(tr("transport.tripScheduled"));
      setOpen(false);
      load();
    } catch (err) {
      const status = err.response?.status;
      toast.error(status === 409 ? `${tr("transport.conflict")}: ${formatApiError(err.response?.data?.detail)}` : formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const setStatus = async (t, status) => {
    try { await api.post(`/transport/trips/${t.id}/status`, { status }); toast.success(`${tr("transport.marked")} ${label(status)}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (t) => {
    try { await api.delete(`/transport/trips/${t.id}`); toast.success(tr("transport.tripDeleted")); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      {canCreate && (
        <div className="flex justify-end mb-4">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-trip-button" className="bg-navy hover:bg-navy-soft text-white gap-2"><Plus className="h-4 w-4" /> {tr("transport.scheduleTrip")}</Button></DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">{tr("transport.scheduleTrip")}</DialogTitle><DialogDescription>{tr("transport.scheduleTripDesc")}</DialogDescription></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-3 mt-2" data-testid="trip-form">
                <div className="space-y-1.5 col-span-2"><Label>{tr("transport.patient")} *</Label>
                  <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                    <SelectTrigger data-testid="trip-patient"><SelectValue placeholder={tr("transport.patient")} /></SelectTrigger>
                    <SelectContent>{refs.patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.patient_code} · {p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>{tr("transport.driver")} *</Label>
                  <Select value={form.driver_id} onValueChange={(v) => setForm({ ...form, driver_id: v })}>
                    <SelectTrigger data-testid="trip-driver"><SelectValue placeholder={tr("transport.driver")} /></SelectTrigger>
                    <SelectContent>{refs.drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>{tr("transport.vehicle")}</Label>
                  <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                    <SelectTrigger data-testid="trip-vehicle"><SelectValue placeholder={tr("common.optional")} /></SelectTrigger>
                    <SelectContent>{refs.vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>{tr("transport.type")}</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="trip-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TRIP_TYPES.map((tt) => <SelectItem key={tt.v} value={tt.v}>{tr(`transport.${tt.l}`)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 grid grid-cols-2 gap-2 col-span-1">
                  <div className="space-y-1.5"><Label>{tr("transport.date")}</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="trip-date" /></div>
                  <div className="space-y-1.5"><Label>{tr("transport.time")}</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
                </div>
                <div className="space-y-1.5 col-span-2"><Label>{tr("transport.pickup")}</Label><Input value={form.pickup_location} onChange={(e) => setForm({ ...form, pickup_location: e.target.value })} data-testid="trip-pickup" /></div>
                <div className="space-y-1.5 col-span-2"><Label>{tr("transport.dropoff")}</Label><Input value={form.dropoff_location} onChange={(e) => setForm({ ...form, dropoff_location: e.target.value })} /></div>
                <DialogFooter className="col-span-2 mt-2"><Button type="submit" disabled={saving} data-testid="trip-submit" className="bg-navy hover:bg-navy-soft text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : tr("transport.schedule")}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>
        : trips.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 p-16 text-center text-slate-400">{tr("transport.noTrips")}</div>
        : (
          <div className="space-y-3" data-testid="trip-list">
            {trips.map((t) => (
              <div key={t.id} data-testid={`trip-${t.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-4 stat-lift">
                <div className="h-10 w-10 rounded-lg bg-navy/5 text-navy flex items-center justify-center shrink-0"><Car className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">{t.patient_name}</span>
                    <Badge className={`${TRIP_STATUS_STYLE[t.status]} font-normal`}>{tr(`driver.s_${t.status}`, label(t.status))}</Badge>
                    <span className="text-xs text-slate-400">{tr(`transport.t_${t.type}`, label(t.type))}</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">{fmt(t.scheduled_time)} · {t.driver_name}{t.vehicle_label ? ` · ${t.vehicle_label}` : ""}</p>
                  {(t.pickup_location || t.dropoff_location) && (
                    <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" />{t.pickup_location} → {t.dropoff_location}</p>
                  )}
                </div>
                {canEdit && !["completed", "cancelled"].includes(t.status) && (
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => setStatus(t, "completed")} data-testid={`complete-trip-${t.id}`}>{tr("transport.complete")}</Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setStatus(t, "cancelled")}>{tr("transport.cancel")}</Button>
                  </div>
                )}
                {canEdit && <button onClick={() => del(t)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function Vehicles({ canCreate, canEdit, tr }) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ plate: "", make_model: "", capacity: 4, color: "" });

  const load = useCallback(() => { setLoading(true); api.get("/transport/vehicles").then((r) => setVehicles(r.data)).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.plate) return toast.error(tr("transport.plateReq"));
    setSaving(true);
    try { await api.post("/transport/vehicles", { ...form, capacity: Number(form.capacity) }); toast.success(tr("transport.vehicleAdded")); setOpen(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (v) => { try { await api.delete(`/transport/vehicles/${v.id}`); toast.success(tr("transport.vehicleRemoved")); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  return (
    <div>
      {canCreate && (
        <div className="flex justify-end mb-4">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-vehicle-button" className="bg-navy hover:bg-navy-soft text-white gap-2"><Plus className="h-4 w-4" /> {tr("transport.addVehicle")}</Button></DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">{tr("transport.addVehicle")}</DialogTitle><DialogDescription>{tr("transport.addVehicleDesc")}</DialogDescription></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-3 mt-2" data-testid="vehicle-form">
                <div className="space-y-1.5 col-span-2"><Label>{tr("transport.plate")} *</Label><Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} data-testid="vehicle-plate" /></div>
                <div className="space-y-1.5 col-span-2"><Label>{tr("transport.makeModel")}</Label><Input value={form.make_model} onChange={(e) => setForm({ ...form, make_model: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{tr("transport.capacity")}</Label><Input type="number" min={1} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{tr("transport.color")}</Label><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
                <DialogFooter className="col-span-2 mt-2"><Button type="submit" disabled={saving} data-testid="vehicle-submit" className="bg-navy hover:bg-navy-soft text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : tr("common.add")}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}
      {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>
        : vehicles.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 p-16 text-center text-slate-400">{tr("transport.noVehicles")}</div>
        : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="vehicle-list">
            {vehicles.map((v) => (
              <div key={v.id} data-testid={`vehicle-${v.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-navy/5 text-navy flex items-center justify-center shrink-0"><Car className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{v.plate}</p>
                  <p className="text-sm text-slate-500">{v.make_model || "—"}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary">{v.capacity} {tr("transport.seats")}</Badge>
                    <Badge className={v.status === "available" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{v.status}</Badge>
                  </div>
                </div>
                {canEdit && <button onClick={() => del(v)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
